<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <!-- <b-card class="m-0">test</b-card> -->
    <component :is="layout">
      <router-view />
    </component>

    <portal-target name="user-plan-sidebars" multiple />
    <portal-target name="nav-tooltips" />

    <quick-search />

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import { BCard } from "bootstrap-vue";
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";
import QuickSearch from "@/components/quick-search/QuickSearch.vue";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch, onUnmounted } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar, useTitle, useFavicon } from "@vueuse/core";

import store from "@/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
    QuickSearch,

    BCard,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    hostname() {
      return window.location.hostname;
    },
  },
  data() {
    return {
      hpgsSmall: require("@/assets/images/logo/hpgs-small.png"),
      szablcSmall: require("@/assets/images/logo/szablc-small.png"),
      whss: require("@/assets/images/logo/whss-logo.png"),
    };
  },
  methods: {
    updateMeta(title, image) {
      const updateTag = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement("meta");
          element.setAttribute("property", property);
          document.head.appendChild(element);
        }
        element.setAttribute("content", content);
      };

      updateTag("og:title", title);

      if (image) {
        // Resolve absolute URL for OG image
        const imageUrl = image.startsWith("data:")
          ? image
          : image.startsWith("http")
          ? image
          : window.location.origin + image;
        updateTag("og:image", imageUrl);
      }
    },
  },
  created() {
    if (this.hostname == "connect.hpgs.pk") {
      useTitle("HPGS Connect");
      useFavicon(this.hpgsSmall);
      this.updateMeta("HPGS Connect", this.hpgsSmall);
    } else if (this.hostname.includes("szablc.edu.pk")) {
      useTitle("SZABLC");
      useFavicon(this.szablcSmall);
      this.updateMeta("SZABLC", this.szablcSmall);
    } else if (
      ["inquiry.whssjuniors.edu.pk", "inquiry.wahajhussains.edu.pk"].includes(
        this.hostname
      )
    ) {
      useTitle("Wahaj Hussain's School System");
      useFavicon(this.whss);
      this.updateMeta("Wahaj Hussain's School System", this.whss);
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    const syncBodySkinClass = () => {
      if (skin.value === "dark") {
        document.body.classList.add("dark-layout");
      } else {
        document.body.classList.remove("dark-layout");
      }
    };

    // If skin is dark when initialized => Add class to body
    syncBodySkinClass();

    // Force no page transition (persisted zoom-fade / page-fade was sticking UI)
    store.commit("appConfig/UPDATE_ROUTER_TRANSITION", "none");

    // Browser tab switches can leave body skin / layout classes out of sync
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncBodySkinClass();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    onUnmounted(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    });

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });
    watch(skin, () => {
      syncBodySkinClass();
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
};
</script>
<style>
.hover-card .card:hover {
  scale: 1.05;
}

.summary-count {
  font-size: 4rem;
  line-height: 0.5;
}

.swal2-actions {
  gap: 10px;
}

.border-dashed {
  border: 1px solid var(--primary);
  border-style: dashed;
}
</style>
